// (c) chaehongee. Confidential and proprietary.

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import type { RootState } from '../store'
import { listAiModels, type AiModelListItem } from '../../services/ai_models'

export interface AiModelsState {
    items: AiModelListItem[]
    currentPage: number
    total: number
    pageSize: number
    loading: boolean
    refreshing: boolean
    error: string | null
}

const PAGE_SIZE = 30

const initialState: AiModelsState = {
    items: [],
    currentPage: 0,
    total: 0,
    pageSize: PAGE_SIZE,
    loading: false,
    refreshing: false,
    error: null,
}

export const fetchAiModelsPage = createAsyncThunk(
    'aiModels/fetchPage',
    async (page: number) => {
        const data = await listAiModels({ page, page_size: PAGE_SIZE })
        return { page, data }
    },
    {
        condition: (page, { getState }) => {
            const state = getState() as { aiModels: AiModelsState }
            // Skip if already loading/refreshing to prevent duplicate requests
            if (state.aiModels.loading || state.aiModels.refreshing) {
                return false
            }
            // Skip if we already have this page loaded
            const hasPage = page <= state.aiModels.currentPage
            return !hasPage
        },
    }
)

export const refreshAiModels = createAsyncThunk(
    'aiModels/refresh',
    async (_, { }) => {
        // Refresh first page to get updated total
        const data = await listAiModels({ page: 1, page_size: PAGE_SIZE })
        return data
    },
    {
        condition: (_, { getState }) => {
            const state = getState() as { aiModels: AiModelsState }
            // Skip if already loading/refreshing
            return !state.aiModels.loading && !state.aiModels.refreshing
        },
    }
)

const aiModelsSlice = createSlice({
    name: 'aiModels',
    initialState,
    reducers: {
        resetAiModels(state) {
            state.items = []
            state.currentPage = 0
            state.total = 0
            state.error = null
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch page
            .addCase(fetchAiModelsPage.pending, (state) => {
                // If we have cached items, this is a background refresh
                if (state.items.length > 0) {
                    state.refreshing = true
                } else {
                    // First load - show loading state
                    state.loading = true
                }
                state.error = null
            })
            .addCase(fetchAiModelsPage.fulfilled, (state, action) => {
                const { page, data } = action.payload
                state.loading = false
                state.refreshing = false

                // Update total from first page
                if (page === 1) {
                    state.total = data.total
                }

                // Avoid duplicates
                const existingIds = new Set(state.items.map((i) => i.id))
                const newItems = data.items.filter((i) => !existingIds.has(i.id))

                state.items = state.items.concat(newItems)
                state.currentPage = Math.max(state.currentPage, page)
            })
            .addCase(fetchAiModelsPage.rejected, (state, action) => {
                state.loading = false
                state.refreshing = false
                state.error = action.error.message || 'Failed to load models'
            })
            // Refresh
            .addCase(refreshAiModels.pending, (state) => {
                state.refreshing = true
                state.error = null
            })
            .addCase(refreshAiModels.fulfilled, (state, action) => {
                state.refreshing = false
                state.total = action.payload.total

                // Update first page items, keep rest
                const firstPageIds = new Set(action.payload.items.map((i) => i.id))
                const restItems = state.items.filter((i) => !firstPageIds.has(i.id))
                state.items = action.payload.items.concat(restItems)
                state.currentPage = Math.max(state.currentPage, 1)
            })
            .addCase(refreshAiModels.rejected, (state, action) => {
                state.refreshing = false
                state.error = action.error.message || 'Failed to refresh models'
            })
    },
})

export const { resetAiModels } = aiModelsSlice.actions

export const selectAiModelsState = (s: RootState) => s.aiModels

export default aiModelsSlice.reducer



